import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const UOMSelector = ({ 
    hsCode: propHsCode, 
    onUOMSelect, 
    value = '', // Add value prop for controlled mode
    className = '',
    disabled = false,
    showLabel = true,
    required = false,
    autoSelect = true, // Add autoSelect prop
}) => {
    const [hsCode, setHsCode] = useState(propHsCode || '');
    const [uomOptions, setUomOptions] = useState([]);
    const [selectedUom, setSelectedUom] = useState(value || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    
    const isAutoSelecting = useRef(false);
    const isUpdatingFromParent = useRef(false);
    const prevValueRef = useRef(value);

    // Sync with external value prop - FIXED: Only update when parent explicitly changes
    useEffect(() => {
        // Only update if the value changed and we're not in the middle of a user selection
        if (value !== prevValueRef.current && !isAutoSelecting.current) {
            isUpdatingFromParent.current = true;
            setSelectedUom(value);
            prevValueRef.current = value;
            setTimeout(() => {
                isUpdatingFromParent.current = false;
            }, 100);
        }
    }, [value]); // Now only depends on value, not selectedUom

    // Fetch UOM data
    const fetchUOMData = useCallback(async (code) => {
        if (!code || !code.trim()) {
            setUomOptions([]);
            setError('');
            return;
        }

        setLoading(true);
        setError('');
        
        try {
            const response = await axios.get('/api/fbr/uom', {
                params: { hs_code: code.trim() }
            });

            if (response.data.success) {
                const options = response.data.data.map(item => ({
                    value: item.uoM_ID,
                    label: item.description,
                    id: item.uoM_ID,
                    description: item.description
                }));
                
                setUomOptions(options);
                
                if (options.length === 0) {
                    setError('No UOM found for this HS Code');
                    setSelectedUom('');
                    if (onUOMSelect) {
                        onUOMSelect(null);
                    }
                } else {
                    setError('');
                    // Auto-select first UOM if enabled and no value is set externally
                    if (autoSelect && !value && !isAutoSelecting.current) {
                        isAutoSelecting.current = true;
                        const firstUom = options[0];
                        setSelectedUom(String(firstUom.value));
                        if (onUOMSelect) {
                            onUOMSelect({
                                id: firstUom.id,
                                description: firstUom.description,
                                hs_code: code
                            });
                        }
                        setTimeout(() => {
                            isAutoSelecting.current = false;
                        }, 100);
                    }
                }
            } else {
                setError(response.data.message || 'Failed to fetch UOM');
                setUomOptions([]);
                setSelectedUom('');
                if (onUOMSelect) {
                    onUOMSelect(null);
                }
            }
        } catch (err) {
            console.error('Error fetching UOM:', err);
            setError('Failed to load UOM data');
            setUomOptions([]);
            setSelectedUom('');
            if (onUOMSelect) {
                onUOMSelect(null);
            }
        } finally {
            setLoading(false);
        }
    }, [autoSelect, value, onUOMSelect]);

    // Sync with prop changes
    useEffect(() => {
        if (propHsCode && propHsCode !== hsCode) {
            setHsCode(propHsCode);
            if (propHsCode) {
                fetchUOMData(propHsCode);
            }
        }
    }, [propHsCode, hsCode, fetchUOMData]);

    // Initial fetch
    useEffect(() => {
        if (propHsCode && !isInitialized) {
            fetchUOMData(propHsCode);
            setIsInitialized(true);
        }
    }, [propHsCode, isInitialized, fetchUOMData]);

    // Handle selection - FIXED: Properly handle user selection
    const handleChange = (e) => {
        const newValue = e.target.value;
        
        // Allow empty selection
        if (newValue === '') {
            setSelectedUom('');
            prevValueRef.current = '';
            if (onUOMSelect) {
                onUOMSelect(null);
            }
            return;
        }
        
        // Find the selected option
        const selected = uomOptions.find(opt => String(opt.value) === String(newValue));
        if (selected) {
            // Update local state
            setSelectedUom(String(selected.value));
            prevValueRef.current = String(selected.value);
            
            // Notify parent
            if (onUOMSelect) {
                onUOMSelect({
                    id: selected.id,
                    description: selected.description,
                    hs_code: hsCode
                });
            }
        }
    };

    // Handle clear selection
    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedUom('');
        prevValueRef.current = '';
        if (onUOMSelect) {
            onUOMSelect(null);
        }
    };

    // Get selected label for display
    const getSelectedLabel = () => {
        if (selectedUom) {
            const selected = uomOptions.find(opt => String(opt.value) === String(selectedUom));
            return selected ? selected.label : '';
        }
        return '';
    };

    // Custom styles
    const styles = {
        container: {
            position: 'relative',
            width: '100%',
        },
        label: {
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '0.35rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
        },
        labelRequired: {
            color: '#ef4444',
            marginLeft: '0.25rem',
        },
        selectWrapper: {
            position: 'relative',
            width: '100%',
        },
        select: {
            width: '100%',
            padding: '0.75rem 3.5rem 0.75rem 1rem',
            fontSize: '0.875rem',
            fontWeight: '400',
            color: '#1e293b',
            backgroundColor: loading 
                ? '#f8fafc' 
                : selectedUom && !loading && !error 
                    ? '#f0fdf4' 
                    : '#ffffff',
            border: `2px solid ${error ? '#ef4444' : isFocused ? '#6366f1' : selectedUom && !loading && !error ? '#10b981' : '#e2e8f0'}`,
            borderRadius: '0.75rem',
            outline: 'none',
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: disabled || loading || uomOptions.length === 0 ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            boxShadow: isFocused 
                ? '0 0 0 4px rgba(99, 102, 241, 0.12)' 
                : selectedUom && !loading && !error 
                    ? '0 0 0 2px rgba(16, 185, 129, 0.1)' 
                    : '0 1px 3px rgba(0, 0, 0, 0.04)',
            minHeight: '48px',
        },
        arrowIcon: {
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: loading ? '#94a3b8' : selectedUom ? '#10b981' : '#94a3b8',
            transition: 'transform 0.25s ease',
        },
        arrowIconRotated: {
            transform: 'translateY(-50%) rotate(180deg)',
        },
        loadingSpinner: {
            position: 'absolute',
            right: '3.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        errorText: {
            marginTop: '0.375rem',
            fontSize: '0.75rem',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
        },
        successText: {
            marginTop: '0.375rem',
            fontSize: '0.75rem',
            color: '#10b981',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
        },
        countBadge: {
            position: 'absolute',
            right: '3.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '0.6rem',
            fontWeight: '600',
            color: '#64748b',
            backgroundColor: '#f1f5f9',
            padding: '0.125rem 0.5rem',
            borderRadius: '9999px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            display: !selectedUom ? 'block' : 'none',
        },
        selectedBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.65rem',
            fontWeight: '600',
            color: '#10b981',
            backgroundColor: '#d1fae5',
            padding: '0.15rem 0.5rem',
            borderRadius: '9999px',
            marginLeft: '0.5rem',
        },
        clearButton: {
            position: 'absolute',
            right: '3.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '0.875rem',
            padding: '0.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease',
            pointerEvents: 'auto',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            zIndex: 2,
        },
    };

    // Loading Spinner Component
    const LoadingSpinner = () => (
        <div style={{ 
            width: '18px', 
            height: '18px',
            border: '2px solid #e2e8f0',
            borderTop: '2px solid #6366f1',
            borderRadius: '50%',
            animation: 'spin 0.7s linear infinite',
        }} />
    );

    // Chevron Down Icon
    const ChevronDown = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );

    // Check Icon
    const CheckIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: '#10b981' }}
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );

    // Clear Icon
    const ClearIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );

    return (
        <div className={className} style={styles.container}>
            {showLabel && (
                <label style={styles.label}>
                    Unit of Measurement
                    {required && <span style={styles.labelRequired}>*</span>}
                    {selectedUom && !loading && !error && (
                        <span style={styles.selectedBadge}>
                            <CheckIcon /> Selected
                        </span>
                    )}
                </label>
            )}

            <div style={styles.selectWrapper}>
                <select
                    value={selectedUom}
                    onChange={handleChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    disabled={disabled || loading || uomOptions.length === 0}
                    style={styles.select}
                    className="uom-select"
                    onMouseEnter={(e) => {
                        if (!disabled && !loading && !error) {
                            e.currentTarget.style.borderColor = isFocused ? '#6366f1' : '#818cf8';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isFocused && !loading && !error) {
                            e.currentTarget.style.borderColor = selectedUom ? '#10b981' : '#e2e8f0';
                        }
                    }}
                >
                    {/* Loading State Option */}
                    {loading && (
                        <option value="" disabled style={{ color: '#94a3b8' }}>
                            ⏳ Loading UOM...
                        </option>
                    )}

                    {/* No Options State */}
                    {!loading && uomOptions.length === 0 && !error && (
                        <option value="" disabled style={{ color: '#94a3b8' }}>
                            {propHsCode ? 'No UOM available' : 'Enter HS Code first'}
                        </option>
                    )}

                    {/* Error State */}
                    {!loading && error && (
                        <option value="" disabled style={{ color: '#ef4444' }}>
                            ⚠️ {error}
                        </option>
                    )}

                    {/* Options */}
                    {!loading && uomOptions.length > 0 && (
                        <>
                            <option value="" style={{ color: '#94a3b8' }}>
                                Select UOM...
                            </option>
                            {uomOptions.map((option) => (
                                <option 
                                    key={option.value} 
                                    value={String(option.value)}
                                    style={{
                                        padding: '8px 12px',
                                        fontWeight: String(selectedUom) === String(option.value) ? '600' : '400',
                                        backgroundColor: String(selectedUom) === String(option.value) ? '#d1fae5' : 'transparent',
                                        color: String(selectedUom) === String(option.value) ? '#065f46' : '#1e293b',
                                    }}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </>
                    )}
                </select>

                {/* Clear Button */}
                {selectedUom && !loading && !isFocused && (
                    <button
                        style={styles.clearButton}
                        onClick={handleClear}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                            e.currentTarget.style.color = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#94a3b8';
                        }}
                        title="Clear selection"
                        type="button"
                    >
                        <ClearIcon />
                    </button>
                )}

                {/* Loading Spinner */}
                {loading && (
                    <div style={styles.loadingSpinner}>
                        <LoadingSpinner />
                    </div>
                )}

                {/* Count Badge */}
                {!loading && uomOptions.length > 0 && !error && !selectedUom && (
                    <div style={styles.countBadge}>
                        {uomOptions.length}
                    </div>
                )}

                {/* Arrow Icon */}
                <div style={{
                    ...styles.arrowIcon,
                    ...(isFocused ? styles.arrowIconRotated : {}),
                }}>
                    <ChevronDown />
                </div>
            </div>

            {/* Error Message */}
            {error && !loading && (
                <div style={styles.errorText}>
                    <span>⚠️</span> {error}
                </div>
            )}

            {/* Success/Info Message */}
            {!loading && !error && selectedUom && (
                <div style={styles.successText}>
                    <CheckIcon /> Selected: {getSelectedLabel()}
                </div>
            )}

            {/* Custom CSS */}
            <style>{`
                .uom-select {
                    background-image: none;
                }
                .uom-select option {
                    padding: 8px 12px;
                }
                .uom-select option:checked {
                    background: #d1fae5;
                    color: #065f46;
                }
                .uom-select:focus option:checked {
                    background: #d1fae5;
                }
                .uom-select option:hover {
                    background: #f1f5f9;
                }
                
                .uom-select::-webkit-scrollbar {
                    width: 6px;
                }
                .uom-select::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                .uom-select::-webkit-scrollbar-thumb {
                    background: #d1d5db;
                    border-radius: 10px;
                }
                .uom-select::-webkit-scrollbar-thumb:hover {
                    background: #9ca3af;
                }

                @keyframes spin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
                .animate-spin {
                    animation: spin 0.8s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default UOMSelector;